/**
 * CalendarModal — the Remote's "Calendar" tile target: the room's upcoming agenda
 * ("what's next in this room"), shown in the same `.scrim` + `.modal` shell as
 * {@link JoinWithIdModal}/{@link DialModal}. It is the Remote-side twin of the TV
 * idle agenda — it fetches the SAME roomkeeper feed the TV does (`GET /api/rooms/
 * :id/calendar`, Bearer = the paired access token) and renders it with the shared
 * {@link TvAgenda} presentation, so the two surfaces can never drift.
 *
 * Fetch-on-open (with an optional light poll while the modal stays open); no push /
 * live-refresh — the room-do control plane doesn't carry calendar. Honest states:
 * a spinner while loading, an inline error on a failed fetch (never a fake "empty"),
 * and TvAgenda's own "No meetings scheduled" when the room simply has nothing
 * (e.g. no calendar linked yet, or the org isn't enrolled).
 */

import type { CalendarEvent } from '@spaces/contracts';
import { useEffect, useRef, useState } from 'react';
import { log } from '../log.js';
import { X } from '../ui/icons.js';
import { TvAgenda } from '../tv/TvAgenda.js';

/** How often the agenda re-fetches while the modal stays open (5 min, TV parity). */
const POLL_MS = 5 * 60 * 1000;

/**
 * Fetch a room's upcoming agenda from roomkeeper via the client Worker's same-origin
 * proxy — the exact call {@link TvApp.refreshAgenda} makes, factored out so it can be
 * unit-tested with a mocked `fetch`. Resolves to the event list (`body.events ?? []`)
 * and REJECTS on a transport error or non-2xx status, so the caller can show an
 * honest error rather than an empty agenda. `tzid` defaults to the browser zone.
 */
export async function fetchRoomAgenda(
    roomId: string,
    accessToken: string,
    signal?: AbortSignal,
    tzid: string = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC'
): Promise<CalendarEvent[]> {
    const res = await fetch(
        `/api/rooms/${encodeURIComponent(roomId)}/calendar?tzid=${encodeURIComponent(tzid)}`,
        { headers: { authorization: `Bearer ${accessToken}` }, signal }
    );

    if (!res.ok) {
        throw new Error(`calendar fetch failed (${res.status})`);
    }

    const body = await res.json() as { events?: CalendarEvent[] };

    return body.events ?? [];
}

export function CalendarModal(props: {
    roomName: string;
    roomId: string;
    accessToken: string;
    onClose: () => void;
}): JSX.Element {
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    // "now" for TvAgenda's Today/day tagging — pinned to the fetch so re-renders stay stable.
    const [now, setNow] = useState<number>(() => Date.now());
    const loadedOnce = useRef<boolean>(false);

    useEffect(() => {
        const ctrl = new AbortController();

        const load = async (): Promise<void> => {
            // Only the first load shows the spinner; a background poll refreshes in place.
            if (!loadedOnce.current) {
                setLoading(true);
            }

            try {
                const next = await fetchRoomAgenda(props.roomId, props.accessToken, ctrl.signal);

                setEvents(next);
                setError(false);
                setNow(Date.now());
                loadedOnce.current = true;
                log.log('Remote agenda loaded', { count: next.length });
            } catch (err) {
                if (ctrl.signal.aborted) {
                    return;
                }

                log.warn('Remote agenda fetch error', { error: String(err) });
                setError(true);
            } finally {
                if (!ctrl.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        void load();
        const timer = setInterval(() => void load(), POLL_MS);

        return () => {
            ctrl.abort();
            clearInterval(timer);
        };
    }, [props.roomId, props.accessToken]);

    return (
        <div className = "scrim" onClick = { props.onClose }>
            <div className = "modal" style = {{ maxWidth: '46cqw', textAlign: 'left' }} onClick = { e => e.stopPropagation() } data-testid = "rm-cal">
                <div className = "row" style = {{ justifyContent: 'space-between', marginBottom: '1.2cqw' }}>
                    <span className = "modal__title" style = {{ margin: 0 }}>Room calendar</span>
                    <span className = "linkbtn" onClick = { props.onClose }><X /></span>
                </div>
                <p className = "modal__body" style = {{ margin: '0 0 1cqw' }}>{ props.roomName }</p>

                { loading ? (
                    <div className = "agenda__empty" data-testid = "rm-cal-loading">Loading…</div>
                ) : error ? (
                    <div className = "agenda__empty" data-testid = "rm-cal-error">Couldn’t load the room calendar. Try again.</div>
                ) : (
                    <TvAgenda events = { events } now = { now } />
                ) }
            </div>
        </div>
    );
}
